import { WorkspaceRoles } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

// Define the roles dropdown
type rolesDropdown = {
  value: WorkspaceRoles;
  description: string;
};
const roles: rolesDropdown[] = [
  { value: 'Viewer', description: 'Can view' },
  { value: 'Editor', description: 'Can view, create and edit' },
  { value: 'Owner', description: 'Admin-level access to all resources' },
];

// Define the form schema
const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  name: z.string().min(2, {
    message: 'Name must be at least 2 characters.',
  }),
  role: z.enum(['Viewer', 'Editor', 'Owner']),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ZendeskSubDomainModal({ open, setOpen }: Props) {
  // query client
  const queryClient = useQueryClient();
  const [subdomain, setSubDomain] = useState('');

  function validateSubdomain(input: string): boolean {
    // Define the regex pattern
    const pattern = /^[a-z0-9](?:[a-z0-9\-]{0,61}[a-z0-9])?$/;

    // Validate the string length
    if (input.length > 63) {
      return false;
    }

    // Validate the string against the regex pattern
    if (!pattern.test(input)) {
      return false;
    }

    return true;
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        setOpen(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Integrate Zendesk</SheetTitle>
          <SheetDescription>
            Please provide your zendesk sub-domain
          </SheetDescription>
        </SheetHeader>

        <div className="mb-4 mt-4 grid grid-cols-3 gap-2">
          <div className="... col-span-2">
            <Input
              placeholder="xyz"
              value={subdomain}
              onChange={e => setSubDomain(e.target.value)}
            />
          </div>
          <div className="flex items-center">
            <p className="text-sm text-muted-foreground">.zendesk.com</p>
          </div>
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline">Cancel</Button>
          </SheetClose>
          <Button
            onClick={() => {
              if (validateSubdomain(subdomain)) {
                const zendeskUrl = new URL(
                  `https://${subdomain}.zendesk.com/oauth/authorizations/new`,
                );
                zendeskUrl.searchParams.append(
                  'client_id',
                  'zdg-tennis-finance',
                );
                zendeskUrl.searchParams.append(
                  'redirect_uri',
                  `${window.location.origin}/zendesk_oauth_redirect`,
                );
                zendeskUrl.searchParams.append('response_type', 'code');
                zendeskUrl.searchParams.append(
                  'state',
                  `subdomain:${subdomain}`,
                );
                zendeskUrl.searchParams.append('response_type', 'code');
                zendeskUrl.searchParams.append('scope', 'read');
                // zendeskUrl.searchParams.append('subdomain', subdomain);
                window.location.href = zendeskUrl.href;
              }
            }}
          >
            Submit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
